import { Button, Popover, PopoverArrow, PopoverContent, PopoverTrigger } from '@civilgrid/ui/components';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import classNames from 'classnames';
import { type MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { LuTruck } from 'react-icons/lu';
import { captureTypedPosthogEvent, POSTHOG_EVENTS, type ReportsMovedPopoverAction } from '~/common/posthogEvents';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import rootStore from '~/store';
import { EXPORTS_MENU_FOCUS_REPORTS, EXPORTS_MENU_SEARCH_PARAM, EXPORTS_MENU_SHOW_REPORTS } from './common';
import { markReportsMovedSeen } from './reportsMovedSeen';

const TRUCK_SIZE = 28;

/** The Reports nav item while the redesign is on: says reports moved to the Exports menu, and links there. */
export default function ReportsMovedNavItem({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  // Matches the menu button's name.
  const labels = useExportLabels();
  const utilitiesPath = `/projects/${projectId}/utilities`;
  const onUtilities = useRouterState({ select: (s) => s.location.pathname === utilitiesPath });
  const [open, setOpen] = useState(false);
  const artRef = useRef<HTMLDivElement>(null);
  // State, not a ref: the portal mounts a render after `open`, so this says when the button exists.
  const [goButton, setGoButton] = useState<HTMLButtonElement | null>(null);
  const [truckLeft, setTruckLeft] = useState<number>();

  // Centers the truck over the main button.
  useLayoutEffect(() => {
    const artEl = artRef.current;
    if (!artEl || !goButton) {
      setTruckLeft(undefined);
      return;
    }
    const place = () => {
      const art = artEl.getBoundingClientRect();
      const go = goButton.getBoundingClientRect();
      setTruckLeft(go.left + go.width / 2 - art.left - TRUCK_SIZE / 2);
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(artEl);
    observer.observe(goButton);
    return () => observer.disconnect();
  }, [goButton]);

  const close = (action: ReportsMovedPopoverAction) => {
    captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesReportsMovedPopoverClosed, {
      project_id: projectId,
      action,
      on_utilities: onUtilities,
    });
    // Escape or a click outside may be accidental, so only a button press counts as seen.
    if (action !== 'dismissed') markReportsMovedSeen(rootStore.me?.id);
    setOpen(false);
  };

  // Trigger, Escape and outside clicks; the buttons close through `close`.
  const onOpenChange = (next: boolean) => {
    if (next) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesReportsMovedPopoverOpened, {
        project_id: projectId,
        on_utilities: onUtilities,
      });
      setOpen(true);
    } else {
      close('dismissed');
    }
  };

  const showMe = (event: MouseEvent) => {
    close('show_me');
    // A keyboard press fires a click with no click count.
    const fromKeyboard = event.detail === 0;
    navigate({
      to: '/projects/$projectId/$tabId',
      params: { projectId, tabId: 'utilities' },
      search: { [EXPORTS_MENU_SEARCH_PARAM]: fromKeyboard ? EXPORTS_MENU_FOCUS_REPORTS : EXPORTS_MENU_SHOW_REPORTS },
    });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={classNames(
            'w-full text-left font-noto text-sm font-normal text-grey-90 flex px-2 py-1 rounded-[6px] transition-all',
            'hover:bg-grey-20 hover:text-primary-dark',
            'focus-ring',
          )}
          data-testid="project-nav-utilities-reports"
        >
          Reports
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        arrowPadding={12}
        alignOffset={-8}
        className="flex max-h-(--radix-popover-content-available-height) w-[300px] flex-col rounded-[10px] p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement)
            .querySelector<HTMLButtonElement>('[data-testid="reports-moved-go"]')
            ?.focus();
        }}
        aria-labelledby="reports-moved-title"
        aria-describedby="reports-moved-description"
        data-testid="reports-moved-popover"
      >
        <section
          aria-labelledby="reports-moved-title"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll area must be focusable to scroll by keyboard (WCAG 2.1.1).
          tabIndex={0}
          className="focus-ring min-h-0 overflow-y-auto rounded-[10px] px-4 pt-4"
          data-testid="reports-moved-body"
        >
          <div ref={artRef} className="relative h-11 overflow-hidden rounded-lg bg-primary" aria-hidden>
            <div className="absolute inset-x-0 bottom-[7px] border-t-2 border-dashed border-white/30" />
            {truckLeft !== undefined && (
              <LuTruck
                size={TRUCK_SIZE}
                className="absolute top-2 text-orange-50 motion-safe:animate-truck-drive"
                style={{ left: truckLeft }}
              />
            )}
          </div>
          <h2 id="reports-moved-title" className="mt-2.5 mb-1 text-base font-semibold text-grey-90">
            Reports have moved!
          </h2>
          <p id="reports-moved-description" className="mb-3.5 text-sm leading-normal text-grey-70">
            They packed up and moved in with Utilities. Open the{' '}
            <b className="font-medium text-grey-90">{labels.navItem}</b> menu at the top of the Utilities page and
            select a report.
          </p>
        </section>
        <div className="flex shrink-0 flex-wrap justify-end gap-2 px-4 pb-4">
          <Button
            variant="secondary"
            className="shrink whitespace-normal"
            onClick={() => close('got_it')}
            data-testid="reports-moved-close"
          >
            Got it
          </Button>
          <Button
            ref={setGoButton}
            className="shrink whitespace-normal"
            onClick={showMe}
            data-testid="reports-moved-go"
          >
            {onUtilities ? 'Show me' : 'Go to Utilities'}
          </Button>
        </div>
        <PopoverArrow width={20} height={10} />
      </PopoverContent>
    </Popover>
  );
}
